import * as THREE from 'three';
import type { ItemDef, PrimitiveGeo } from '../content/types';
import { primitiveGeometry, toonMaterial } from './geometry';
import type { ModelLibrary } from './models';

export interface EquipmentPresentation {
  weaponId: string;
  armorId: string | null;
  relicId: string | null;
}

export function equipmentPresentation(items: readonly ItemDef[] | undefined): EquipmentPresentation {
  return {
    weaponId: items?.find((item) => item.slot === 'weapon')?.id ?? 'w_spore_launcher',
    armorId: items?.find((item) => item.slot === 'armor')?.id ?? null,
    relicId: items?.find((item) => item.slot === 'relic')?.id ?? null,
  };
}

function mesh(geo: THREE.BufferGeometry, color: number, emissive = 0, intensity = 0): THREE.Mesh {
  const out = new THREE.Mesh(geo, toonMaterial(color, emissive, intensity));
  out.castShadow = true;
  return out;
}

function part(group: THREE.Group, geo: THREE.BufferGeometry, color: number, position: [number, number, number], scale: [number, number, number], rotation?: [number, number, number], emissive = 0, intensity = 0, owned = true): THREE.Mesh {
  if (owned) geo.userData.equipmentOwned = true;
  const out = mesh(geo, color, emissive, intensity);
  out.position.set(...position);
  out.scale.set(...scale);
  if (rotation) out.rotation.set(...rotation);
  group.add(out);
  return out;
}

/** Compact code-native weapon silhouettes. They can later be replaced by ItemDef.visual.gltf. */
function buildWeapon(id: string): THREE.Group {
  const g = new THREE.Group();
  switch (id) {
    case 'w_bone_spear':
      part(g, new THREE.CylinderGeometry(0.035, 0.045, 1.45, 6), 0x8d6945, [0, 0, 0], [1, 1, 1], [Math.PI / 2, 0, 0]);
      part(g, new THREE.ConeGeometry(0.13, 0.42, 5), 0xffe5a3, [0, 0, 0.92], [1, 1, 1], [Math.PI / 2, 0, 0]);
      break;
    case 'w_bronze_bow':
      part(g, new THREE.TorusGeometry(0.36, 0.035, 5, 18, Math.PI * 1.35), 0xd79a53, [0, 0, 0.35], [1, 1, 1], [0, 0, Math.PI / 2]);
      part(g, new THREE.CylinderGeometry(0.022, 0.022, 1.05, 5), 0xffd27d, [0, 0, 0.42], [1, 1, 1], [Math.PI / 2, 0, 0]);
      break;
    case 'w_crossbow':
      part(g, new THREE.BoxGeometry(0.14, 0.13, 0.95), 0x6f7f89, [0, 0, 0.42], [1, 1, 1]);
      part(g, new THREE.BoxGeometry(0.95, 0.07, 0.12), 0xa6b7c2, [0, 0, 0.68], [1, 1, 1]);
      part(g, new THREE.CylinderGeometry(0.025, 0.025, 1.25, 5), 0x62e5ff, [0, 0.08, 0.62], [1, 1, 1], [Math.PI / 2, 0, 0], 0x62e5ff, 0.25);
      break;
    case 'w_flintlock':
      part(g, new THREE.BoxGeometry(0.13, 0.13, 0.9), 0x75513e, [0, 0, 0.38], [1, 1, 1]);
      part(g, new THREE.CylinderGeometry(0.065, 0.075, 0.76, 8), 0xb7a48f, [0, 0.03, 0.86], [1, 1, 1], [Math.PI / 2, 0, 0]);
      part(g, new THREE.BoxGeometry(0.13, 0.42, 0.16), 0x5f3e2e, [0, -0.2, 0.12], [1, 1, 1], [0.35, 0, 0]);
      break;
    case 'w_pulse_rifle':
      part(g, new THREE.BoxGeometry(0.22, 0.2, 0.94), 0x294b63, [0, 0, 0.42], [1, 1, 1]);
      part(g, new THREE.BoxGeometry(0.08, 0.08, 1.12), 0x8cff9e, [0, 0.09, 0.52], [1, 1, 1], undefined, 0x8cff9e, 0.65);
      part(g, new THREE.BoxGeometry(0.3, 0.08, 0.28), 0x62e5ff, [0, -0.04, 0.18], [1, 1, 1], undefined, 0x62e5ff, 0.3);
      break;
    case 'w_chrono_cannon':
      part(g, new THREE.CylinderGeometry(0.18, 0.25, 0.82, 10), 0x392b5c, [0, 0, 0.45], [1, 1, 1], [Math.PI / 2, 0, 0]);
      part(g, new THREE.TorusGeometry(0.27, 0.055, 6, 16), 0xc9a7ff, [0, 0, 0.82], [1, 1, 1], [Math.PI / 2, 0, 0], 0x9d72ff, 0.65);
      part(g, new THREE.SphereGeometry(0.13, 8, 6), 0xffffff, [0, 0, 0.88], [1, 1, 1], undefined, 0xc9a7ff, 1);
      break;
    default:
      part(g, new THREE.CapsuleGeometry(0.13, 0.55, 2, 6), 0x397d68, [0, 0, 0.42], [1, 1, 1], [Math.PI / 2, 0, 0]);
      part(g, primitiveGeometry('blob'), 0x79e9b5, [0, 0, 0.82], [0.32, 0.32, 0.32], undefined, 0x2c9b76, 0.45, false);
      break;
  }
  return g;
}

export class EquipmentRig {
  readonly group = new THREE.Group();
  private readonly weapon: THREE.Group;
  private readonly armor: THREE.Mesh | null;
  private readonly relic: THREE.Mesh | null;
  private readonly materials: THREE.Material[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];

  constructor(items: readonly ItemDef[] | undefined, models?: ModelLibrary) {
    const equipped = equipmentPresentation(items);
    const weaponItem = items?.find((item) => item.id === equipped.weaponId);
    const weaponModel = weaponItem?.visual?.gltf && models?.has(weaponItem.visual.gltf)
      ? new THREE.Mesh(models.geometryFor(weaponItem.visual), models.materialFor(weaponItem.visual))
      : null;
    this.weapon = weaponModel ? new THREE.Group() : buildWeapon(equipped.weaponId);
    if (weaponModel) {
      weaponModel.castShadow = true;
      weaponModel.scale.setScalar(1.15);
      weaponModel.position.y = 0.5;
      this.weapon.add(weaponModel);
    }
    this.weapon.position.set(0.48, 1.02, 0.32);
    this.weapon.scale.setScalar(0.78);
    this.group.add(this.weapon);

    const armorItem = items?.find((item) => item.id === equipped.armorId);
    if (armorItem?.visual) {
      const usingModel = !!armorItem.visual.gltf && !!models?.has(armorItem.visual.gltf);
      const geometry = usingModel
        ? models!.geometryFor(armorItem.visual)
        : armorItem.visual.geo === 'torus'
        ? new THREE.TorusGeometry(0.56, 0.05, 7, 20)
        : primitiveGeometry(armorItem.visual.geo as PrimitiveGeo);
      if (!usingModel && armorItem.visual.geo === 'torus') geometry.userData.equipmentOwned = true;
      const material = usingModel
        ? models!.materialFor(armorItem.visual)
        : toonMaterial(armorItem.visual.color, armorItem.visual.emissive ?? armorItem.visual.color, Math.max(0.08, armorItem.visual.emissiveIntensity ?? 0.08));
      material.transparent = true;
      material.opacity = 0.34;
      material.depthWrite = false;
      this.armor = new THREE.Mesh(geometry, material);
      this.armor.position.y = 0.92;
      this.armor.scale.set(1.02, 1.45, 0.72);
      this.group.add(this.armor);
    } else this.armor = null;

    const relicItem = items?.find((item) => item.id === equipped.relicId);
    if (relicItem?.visual) {
      const usingModel = !!relicItem.visual.gltf && !!models?.has(relicItem.visual.gltf);
      const material = usingModel
        ? models!.materialFor(relicItem.visual)
        : toonMaterial(relicItem.visual.color, relicItem.visual.emissive ?? relicItem.visual.color, Math.max(0.25, relicItem.visual.emissiveIntensity ?? 0.25));
      this.relic = new THREE.Mesh(usingModel ? models!.geometryFor(relicItem.visual) : primitiveGeometry(relicItem.visual.geo), material);
      this.relic.scale.setScalar(0.26);
      this.group.add(this.relic);
    } else this.relic = null;

    this.group.traverse((obj) => {
      const candidate = obj as THREE.Mesh;
      if (!candidate.isMesh) return;
      const mats = Array.isArray(candidate.material) ? candidate.material : [candidate.material];
      this.materials.push(...mats);
      if (candidate.geometry.userData.equipmentOwned) this.geometries.push(candidate.geometry);
    });
  }

  update(t: number, firing: boolean, dashing: boolean): void {
    this.weapon.position.z = 0.32 - (firing ? 0.06 + 0.025 * Math.sin(t * 38) : 0);
    this.weapon.rotation.z = dashing ? -0.45 : 0;
    if (this.armor) {
      const pulse = 1 + Math.sin(t * 3.5) * 0.025;
      this.armor.scale.set(1.02 * pulse, 1.45, 0.72 * pulse);
      this.armor.rotation.y = Math.sin(t * 1.2) * 0.08;
    }
    if (this.relic) {
      const a = t * 1.8;
      this.relic.position.set(-0.58 + Math.cos(a) * 0.15, 1.3 + Math.sin(a * 1.7) * 0.12, Math.sin(a) * 0.24);
      this.relic.rotation.set(a * 0.4, a, a * 0.25);
    }
  }

  dispose(): void {
    for (const material of new Set(this.materials)) material.dispose();
    for (const geometry of new Set(this.geometries)) geometry.dispose();
    this.group.removeFromParent();
  }
}
